// Utilities for normalizing US state input across the app.
// Goal: prevent abbreviations like "FL" from being persisted as new "states"
// when the system expects full state names like "Florida".

export const US_STATE_ABBR_TO_NAME = Object.freeze({
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia",
  PR: "Puerto Rico",
  VI: "U.S. Virgin Islands",
  GU: "Guam",
  AS: "American Samoa",
  MP: "Northern Mariana Islands",
});

/**
 * Normalize a US state input value.
 *
 * - Trims whitespace
 * - If `allowedStates` is provided, returns the canonical string from that list
 *   (case-insensitive match), so casing matches backend expectations.
 * - Converts 2-letter abbreviations (e.g. "FL") to full names (e.g. "Florida")
 * - Otherwise returns the trimmed input
 */
export function normalizeUsState(input, allowedStates) {
  const raw = (input ?? "").toString().trim();
  if (!raw) return "";

  // If it's already a valid allowed state (case-insensitive), return the canonical
  if (Array.isArray(allowedStates) && allowedStates.length) {
    const direct = allowedStates.find(
      (s) => (s ?? "").toString().trim().toLowerCase() === raw.toLowerCase()
    );
    if (direct) return direct;
  }

  // Abbreviation → full name
  const upper = raw.toUpperCase();
  const fullName = US_STATE_ABBR_TO_NAME[upper];
  if (fullName) {
    if (Array.isArray(allowedStates) && allowedStates.length) {
      const canonical = allowedStates.find(
        (s) => (s ?? "").toString().trim().toLowerCase() === fullName.toLowerCase()
      );
      return canonical || fullName;
    }
    return fullName;
  }

  return raw;
}

export function isAllowedUsState(stateValue, allowedStates) {
  if (!stateValue) return false;
  if (!Array.isArray(allowedStates) || allowedStates.length === 0) return true;
  const v = stateValue.toString().trim().toLowerCase();
  return allowedStates.some(
    (s) => (s ?? "").toString().trim().toLowerCase() === v
  );
}

